// @ts-nocheck
/**
 * useImageAnalysis - 图片分析 Hook
 *
 * 管理图片截取、canvas 交互、AI 分析和保存标注的完整流程。
 * 从 PDFViewerWithAI 中提取的 UI 交互逻辑。
 */

import { useState, useCallback } from 'react';
import html2canvas from 'html2canvas';
import { apiPost } from '../utils/apiClient';
import { logger } from '../utils/logger';
import type { AnnotationsMap } from '../types/pdf-viewer';

interface SelectionRect {
  startX: number;
  startY: number;
  currentX: number;
  currentY: number;
}

interface SelectionStyle {
  left?: string;
  top?: string;
  width?: string;
  height?: string;
}

interface UseImageAnalysisOptions {
  onNotify?: (message: string) => void;
  getPageRef?: () => HTMLElement | null;
  getCurrentPage?: () => number;
  getPdfFingerprint?: () => string;
  getAnnotations?: () => AnnotationsMap;
  setAnnotations?: (annotations: AnnotationsMap) => void;
  saveAnnotationsToStorage?: ((fingerprint: string, annotations: AnnotationsMap) => Promise<boolean>) | null;
}

export function useImageAnalysis({
  onNotify = () => {},
  getPageRef = () => null,
  getCurrentPage = () => 1,
  getPdfFingerprint = () => '',
  getAnnotations = () => ({}),
  setAnnotations = () => {},
  saveAnnotationsToStorage = null,
}: UseImageAnalysisOptions = {}) {
  const [showImagePanel, setShowImagePanel] = useState<boolean>(false);
  const [isSelecting, setIsSelecting] = useState<boolean>(false);
  const [selectionRect, setSelectionRect] = useState<SelectionRect | null>(null);
  const [capturedImage, setCapturedImage] = useState<string | null>(null);
  const [imageQuestion, setImageQuestion] = useState<string>('');
  const [imageAnswer, setImageAnswer] = useState<string>('');
  const [imageQuestioning, setImageQuestioning] = useState<boolean>(false);

  /**
   * 开始图片选择（进入截图模式）
   */
  const startImageSelection = useCallback(() => {
    logger.log('开始图片选择');
    setIsSelecting(true);
    setSelectionRect(null);
    setCapturedImage(null);
    setImageQuestion('');
  }, []);

  /**
   * 取消图片选择
   */
  const cancelImageSelection = useCallback(() => {
    setIsSelecting(false);
    setSelectionRect(null);
  }, []);

  /**
   * 重新截图（清除已捕获的图片）
   */
  const retakeImage = useCallback(() => {
    setCapturedImage(null);
    setImageQuestion('');
    setImageAnswer('');
  }, []);

  /**
   * 截取选中区域的图片
   */
  const captureSelectedAreaWithRect = useCallback(async (selectionData: SelectionRect) => {
    const pageElement = getPageRef();
    if (!pageElement || !selectionData) {
      logger.log('截图条件不满足');
      return;
    }

    logger.log('开始截图处理', selectionData);

    try {
      const canvas = await html2canvas(pageElement, {
        useCORS: true,
        scale: 2,
        backgroundColor: null
      });

      const { startX, startY, currentX, currentY } = selectionData;
      const left = Math.min(startX, currentX);
      const top = Math.min(startY, currentY);
      const width = Math.abs(currentX - startX);
      const height = Math.abs(currentY - startY);

      logger.log('截图区域', { left, top, width, height });

      if (width < 10 || height < 10) {
        logger.log('选择区域太小');
        onNotify('选择区域太小，请重新选择');
        setIsSelecting(false);
        setSelectionRect(null);
        return;
      }

      const croppedCanvas = document.createElement('canvas');
      const ctx = croppedCanvas.getContext('2d')!;
      croppedCanvas.width = width * 2;
      croppedCanvas.height = height * 2;

      ctx.drawImage(
        canvas,
        left * 2, top * 2, width * 2, height * 2,
        0, 0, width * 2, height * 2
      );

      const imageDataUrl = croppedCanvas.toDataURL('image/png');
      logger.log('截图完成，数据长度:', imageDataUrl.length);

      setCapturedImage(imageDataUrl);
      setIsSelecting(false);
      setSelectionRect(null);

      onNotify('截图完成，请输入问题');

    } catch (error) {
      console.error('截图失败:', error);
      onNotify('截图失败，请重试');
      setIsSelecting(false);
      setSelectionRect(null);
    }
  }, [getPageRef, onNotify]);

  /**
   * 鼠标按下处理（截图模式下的拖拽选择）
   */
  const handleMouseDown = useCallback((e: React.MouseEvent) => {
    const pageElement = getPageRef();
    if (!isSelecting || !pageElement) {
      logger.log('鼠标按下但不在截图模式');
      return;
    }

    logger.log('截图模式鼠标按下');
    e.preventDefault();
    e.stopPropagation();

    const rect = pageElement.getBoundingClientRect();
    const startX = e.clientX - rect.left;
    const startY = e.clientY - rect.top;

    let currentSelection: SelectionRect = {
      startX, startY,
      currentX: startX,
      currentY: startY
    };

    setSelectionRect(currentSelection);

    const handleMouseMove = (e: MouseEvent) => {
      const currentX = e.clientX - rect.left;
      const currentY = e.clientY - rect.top;

      currentSelection = {
        ...currentSelection,
        currentX,
        currentY
      };

      setSelectionRect(currentSelection);
    };

    const handleMouseUp = () => {
      logger.log('截图鼠标释放', currentSelection);
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);

      captureSelectedAreaWithRect(currentSelection);
    };

    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);
  }, [isSelecting, getPageRef, captureSelectedAreaWithRect]);

  /**
   * 执行图片问答（发送到 AI）
   */
  const handleImageQuestion = useCallback(async () => {
    if (!capturedImage || !imageQuestion.trim()) {
      onNotify('请确保已截图并输入问题');
      return;
    }

    setImageQuestioning(true);

    try {
      const result = await apiPost<{ answer: string }>('/api/ask-image-question', {
        image: capturedImage,
        question: imageQuestion,
      });
      setImageAnswer(result.answer);
      onNotify('图片分析完成');
    } catch (error) {
      logger.error('图片问答失败:', error);
      const errMsg = (error as Error).message;
      setImageAnswer('抱歉，图片问答服务出现错误：' + errMsg);
      onNotify('图片分析失败: ' + errMsg);
    } finally {
      setImageQuestioning(false);
    }
  }, [capturedImage, imageQuestion, onNotify]);

  /**
   * 保存图片标注
   */
  const saveImageAsAnnotation = useCallback(async () => {
    if (!capturedImage || !imageQuestion || !imageAnswer) {
      onNotify('请确保有完整的图片问答内容');
      return;
    }

    const pdfFingerprint = getPdfFingerprint();
    if (!pdfFingerprint) {
      onNotify('PDF指纹生成中，请稍候重试');
      return;
    }

    const currentPage = getCurrentPage();
    const annotationId = `img_${currentPage}_${Date.now()}`;

    const newAnnotation = {
      id: annotationId,
      page: currentPage,
      text: '[图片内容]',
      question: imageQuestion,
      answer: imageAnswer,
      tag: 'note',
      timestamp: new Date().toISOString(),
      pdfFingerprint: pdfFingerprint,
      type: 'image',
      image: capturedImage,
      hasHighlight: false,
      highlightRects: [],
      position: null,
    };

    const annotations = getAnnotations();
    const newAnnotations: AnnotationsMap = {
      ...annotations,
      [annotationId]: newAnnotation
    };

    if (saveAnnotationsToStorage) {
      const saved = await saveAnnotationsToStorage(pdfFingerprint, newAnnotations);
      if (saved) {
        setAnnotations(newAnnotations);
        onNotify('图片标注已保存');
        setCapturedImage(null);
        setImageQuestion('');
        setImageAnswer('');
      } else {
        onNotify('保存失败，请重试');
      }
    }
  }, [capturedImage, imageQuestion, imageAnswer, onNotify, getPdfFingerprint, getCurrentPage, getAnnotations, setAnnotations, saveAnnotationsToStorage]);

  /**
   * 关闭图片面板并重置所有状态
   */
  const closeImagePanel = useCallback(() => {
    setShowImagePanel(false);
    setIsSelecting(false);
    setSelectionRect(null);
    setCapturedImage(null);
    setImageQuestion('');
    setImageAnswer('');
  }, []);

  /**
   * 计算选择框样式
   */
  const getSelectionStyle = useCallback((): SelectionStyle => {
    if (!selectionRect) return {};

    const { startX, startY, currentX, currentY } = selectionRect;
    const left = Math.min(startX, currentX);
    const top = Math.min(startY, currentY);
    const width = Math.abs(currentX - startX);
    const height = Math.abs(currentY - startY);

    return {
      left: `${left}px`,
      top: `${top}px`,
      width: `${width}px`,
      height: `${height}px`
    };
  }, [selectionRect]);

  return {
    // 状态
    showImagePanel,
    isSelecting,
    selectionRect,
    capturedImage,
    imageQuestion,
    imageAnswer,
    imageQuestioning,
    // 状态设置器（供外部直接控制）
    setShowImagePanel,
    setIsSelecting,
    setSelectionRect,
    setCapturedImage,
    setImageQuestion,
    setImageAnswer,
    // 交互方法
    startImageSelection,
    cancelImageSelection,
    retakeImage,
    captureSelectedAreaWithRect,
    handleMouseDown,
    handleImageQuestion,
    saveImageAsAnnotation,
    closeImagePanel,
    getSelectionStyle,
  };
}

export default useImageAnalysis;
